<template>
  <div class="new-rangeSelect pt-4">
    <!-- Grid of 5 clickable segments (one per optionsLength entry); classes highlight the middle/neutral segment and red/green states based on the current selection -->
    <div class="new-rangeSelect-wrp" :style="{ gridTemplateColumns: `repeat(${5}, 1fr)` }">
      <!-- v-for renders one segment per entry in `optionsLength`; clicking a segment calls newHandleSetValue to update the v-model value -->
      <div
        v-for="(el, index) in optionsLength"
        :key="el.level"
        class="new-rangeSelect-el"
        :class="{
          level0: index === 2,
          levelBord1: index === 1,
          levelBord2: index === 3,
          redLevel1: reactionsLevel === -1 && optionsLength[index].value === -1,
          redLevel2: reactionsLevel === -2 && optionsLength[index].value === -2,
          greenLevel1: reactionsLevel === 1 && optionsLength[index].level === 3,
          greenLevel2: reactionsLevel === 2 && optionsLength[index].level >= 4,
        }"
        @click="newHandleSetValue(el.value, el.level)">
        <!-- Colored track segment for this option -->
        <div class="new-rangeSelect-el-line" :class="`color-${Math.abs(el.value)}`"></div>
        <!-- Numeric label for this segment; 'selected' class applied once the current model value reaches/exceeds this segment's magnitude -->
        <div
          class="new-rangeSelect-el-txt"
          :class="
            clsx(
              {
                selected: model >= Math.abs(el.value),
              },
              `color-${el.level}`,
            )
          ">
          {{ Math.abs(el.value) }}
        </div>
      </div>
    </div>

    <!-- Indicator dot that slides to `positionBullet` to mark the current model value along the track -->
    <span class="new-rangeSelect-el-circle" :class="`color-${model}`" :style="{ left: positionBullet }"></span>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';

import clsx from 'clsx';

/** The 5 selectable range options, each with a display `level` (0-4) and the underlying `value` (-2..2) it maps to. */
const optionsLength = [
  { level: 0, value: -2 },
  { level: 1, value: -1 },
  { level: 2, value: 0 },
  { level: 3, value: 1 },
  { level: 4, value: 2 },
];
// Positions for the circle (center of each section)
/** Horizontal percentage positions for the indicator circle, one per level (index-matched to `optionsLength`). */
const positionsBullet = [10, 30, 50, 70, 90];

/** Current selected value (-2..2), used to drive the red/green highlight classes on the segments. */
const reactionsLevel = ref(0);
/**
 * @prop {number} modelValue - The selected range value (-2..2), bound via `v-model` (defineModel).
 */
const model = defineModel();
/** Whether the current level is on the "red" (negative) side of the scale; kept in sync by the `level` watcher below. */
const isRed = ref(false);

// Initialize level based on model value
/** Current selected level index (0-4), initialized to 2 (neutral). */
const level = ref(2); // Default to neutral (level 2)

// Update level when model changes
/**
 * Runs whenever `model` (the bound v-model value) changes, including once
 * immediately on mount: looks up the matching option and syncs `level` and
 * `reactionsLevel` so the UI reflects the external/initial value.
 */
watch(model, (newValue) => {
  // Find the matching option based on the model value
  const matchingOption = optionsLength.find(opt => opt.value === newValue);
  if (matchingOption) {
    level.value = matchingOption.level;
    reactionsLevel.value = newValue;
  }
}, { immediate: true });

// Keep side effects out of computed
/** Runs whenever `level` changes: updates `isRed` to reflect whether the new level is on the negative side. */
watch(level, (lv) => {
  isRed.value = lv < 2;
});

/** CSS `left` percentage string for the indicator circle, derived from the current `level`. */
const positionBullet = computed(() => {
  if (level.value >= 0 && level.value < positionsBullet.length) {
    return positionsBullet[level.value] + '%';
  }
  return '0px';
});

/**
 * Handles a click on one of the range segments: updates the local
 * `reactionsLevel`/`level` state and writes the new value back through
 * `model` (propagating to the parent via v-model).
 * @param {number} value - The segment's underlying value (-2..2)
 * @param {number} position - The segment's level index (0-4)
 */
function newHandleSetValue(value, position) {
  reactionsLevel.value = value;
  level.value = position;
  model.value = value;
}
</script>

<style scoped lang="scss">
.new-rangeSelect {
  position: relative;

  &-wrp {
    display: grid;
    width: 100%;
  }

  &.isRed {
    --color: #931919;
    --color_2: #b72020;
    --color_3: #dc2626;
    --borderSelected: #e24a4a;
    --colorHovered_1: #715353;
    --colorHovered_2: #835656;
  }

  &.isGreen {
    --color: var(--color-brand-secondary);
    --color_2: var(--color-brand-secondary);
    --color_3: var(--color-brand-secondary);
    --borderSelected: var(--color-brand-secondary);
    --colorHovered_1: var(--color-brand-secondary);
    --colorHovered_2: var(--color-brand-secondary);
  }

&.hasSelected {
  .new-rangeSelect-el-line {
    background: var(--colorHovered);
    border-color: var(--colorHovered);
    /* OLD: border-left-color: #959595; */
    border-left-color: var(--color-text-muted);
  }
}

&-el {
    cursor: pointer;
    position: relative;
    &.level0 .new-rangeSelect-el-line {
      /* OLD: border: 2px solid #1d1d21; */
      border: 2px solid var(--color-grey-senary);
      border-radius: 10px;
      height: 14px;
      margin-top: 13px; /* Adjust to match other lines */
    }
    &.levelBord1 .new-rangeSelect-el-line {
      border-top-right-radius: 10px;
      border-bottom-right-radius: 10px;
    }
    &.levelBord2 .new-rangeSelect-el-line {
      border-top-left-radius: 10px;
      border-bottom-left-radius: 10px;
    }
    &.redLevel1 .new-rangeSelect-el-line {
      /* OLD: background-color: #931919; */
      background-color: var(--color-error-text);
      /* OLD: border-color: #931919; */
      border-color: var(--color-error-text);
      box-shadow: 0 0 7px #931919;
    }
    &.redLevel2 .new-rangeSelect-el-line {
      /* OLD: background-color: #b72020; */
      background-color: var(--color-error-text);
      /* OLD: border-color: #b72020; */
      border-color: var(--color-error-text);
      box-shadow: 0 0 7px #b72020;
    }
    &.redLevel3 .new-rangeSelect-el-line {
      /* OLD: background-color: #dc2626; */
      background-color: var(--color-error-text);
      /* OLD: border-color: #dc2626; */
      border-color: var(--color-error-text);
      box-shadow: 0 0 7px #dc2626;
    }
    &.greenLevel1 .new-rangeSelect-el-line {
      background-color: var(--color-brand-secondary);
      border-color: var(--color-brand-secondary);
      box-shadow: 0 0 7px var(--color-brand-secondary);
    }
    &.greenLevel2 .new-rangeSelect-el-line {
      background-color: var(--color-brand-secondary);
      border-color: var(--color-brand-secondary);
      box-shadow: 0 0 7px var(--color-brand-secondary);
    }
    &.greenLevel3 .new-rangeSelect-el-line {
      background-color: var(--color-brand-secondary);
      border-color: var(--color-brand-secondary);
      box-shadow: 0 0 7px var(--color-brand-secondary);
    }

    &-circle {
      /* OLD: border: 2px solid #fff; */
      border: 2px solid var(--color-white);
      width: 14px; /* A bit larger for better visibility */
      pointer-events: none;
      border-radius: 50%;
      background: var(--color);
      position: absolute;
      top: 37px;
      height: 14px; /* A bit larger for better visibility */
      transition: left 0.3s ease-in-out;
      z-index: 2;
      /* OLD: box-shadow: 0 0 5px #fff; */
      box-shadow: 0 0 5px var(--color-white);
      transform: translateX(-50%) translateY(-50%); /* Center the circle horizontally and vertically */
    }

    &-txt {
      text-align: center;
      margin-top: 4px;
      font-size: 12px;
      /* OLD: color: #a1a1aa; */
      color: var(--color-grey-quinary);
      transition: 0.2s;
    }

    &:nth-child(1) {
      .new-rangeSelect-el-line {
        border-radius: 40px 0 0 40px;
        border-left: none;
      }
    }

    &:last-child {
      .new-rangeSelect-el-line {
        border-radius: 0 40px 40px 0;
      }
    }

    &-line {
      /* OLD: background: #464648; */
      background: var(--color-grey-denary);
      /* OLD: border: 2px solid #464648; */
      border: 2px solid var(--color-grey-denary);
      height: 10px;
      position: relative;
      z-index: 0;
      transition: 0.15s;
      margin-top: 15px; /* Position the line to align with the circle */
    }

    &.selected {
      .new-rangeSelect-el-line {
        box-shadow: 0 0 7px var(--colorSelected);
        background: var(--colorSelected);
        border: 2px solid var(--colorSelected);
      }
    }

    &.active {
      .new-rangeSelect-el-line {
        background: var(--color);
      }
    }
  }
}
</style>
